import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import ProfileSection from './components/ProfileSection';
import SecuritySection from './components/SecuritySection';
import AppearanceSection from './components/AppearanceSection';
import PreferencesSection from './components/PreferencesSection';
import BillingSection from './components/BillingSection';
import { useAuth } from '../../contexts/AuthContext';
import CategoriesSection from './components/CategoriesSection';
import AboutUsSection from './components/AboutUsSection';
import TermsSection from './components/TermsSection';


const Settings = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [activeSection, setActiveSection] = useState('profile');

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Settings', path: '/settings' }
  ];

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'security', label: 'Security', icon: 'Lock' },
    { id: 'appearance', label: 'Appearance', icon: 'Palette' },
    { id: 'preferences', label: 'Preferences', icon: 'Settings' },
    { id: 'categories', label: 'Categories', icon: 'Tag' },
    { id: 'billing', label: 'Billing', icon: 'CreditCard' },
    { id: 'about', label: 'About Us', icon: 'Info' },
    { id: 'terms', label: 'Terms of Service', icon: 'FileText' }
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-16"></div>
          <p className="text-muted-foreground font-caption">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Settings - Rovyn Budget</title>
        <meta name="description" content="Manage your account settings, security preferences, and application appearance" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="mx-auto px-16 md:px-24 py-24 md:py-32 max-w-[1400px]">
            <Breadcrumbs items={breadcrumbItems} />

            <div className="flex items-center justify-between flex-wrap gap-16 mb-24 md:mb-32">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-8">
                  Settings
                </h1>
                <p className="text-sm md:text-base text-muted-foreground font-caption">
                  Manage your account preferences and application settings
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-24">
              <div className="lg:col-span-1">
                <div className="bg-card rounded-lg shadow-elevation-2 p-16 sticky top-24">
                  <nav className="space-y-8">
                    {sections?.map((section) => (
                      <button
                        key={section?.id}
                        onClick={() => setActiveSection(section?.id)}
                        className={`
                          w-full flex items-center gap-12 px-16 py-12 rounded-lg
                          transition-smooth font-caption font-medium
                          ${activeSection === section?.id
                            ? 'bg-primary/10 text-primary' :'text-foreground hover:bg-muted'
                          }
                        `}
                      >
                        <Icon
                          name={section?.icon}
                          size={20}
                          color={activeSection === section?.id ? 'var(--color-primary)' : 'currentColor'}
                          strokeWidth={activeSection === section?.id ? 2.5 : 2}
                        />
                        <span>{section?.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              <div className="lg:col-span-3">
                {activeSection === 'profile' && <ProfileSection />}
                {activeSection === 'security' && <SecuritySection />}
                {activeSection === 'appearance' && <AppearanceSection />}
                {activeSection === 'preferences' && <PreferencesSection />}
                {activeSection === 'categories' && <CategoriesSection />}
                {activeSection === 'billing' && <BillingSection />}
                {activeSection === 'about' && <AboutUsSection />}
                {activeSection === 'terms' && <TermsSection />}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Settings;